import { makeVar, ReactiveVar } from '@apollo/client';
import { usePermitStore } from 'stores/permitStore';
import { ConfirmCreateSalesOrderDataProps } from 'types/common';
import { LicensePlate } from 'types/generatedSchemaTypes';

export const parkingVehicleLicensePlate: ReactiveVar<string> =
  makeVar<string>('');
export const parkingPaymentCardId: ReactiveVar<string> = makeVar<string>('');

export const confirmCreateSalesOrderData: ReactiveVar<ConfirmCreateSalesOrderDataProps> =
  makeVar<ConfirmCreateSalesOrderDataProps>(undefined);

/**
 * Tracks the last server-derived plate snapshot applied in permit edit so we
 * do not overwrite `permitStore.selectedLicensePlatesPermitRedeem` on web when
 * the edit screen remounts after `permitVehicles` (same server data, user
 * already chose plates). Cleared when permit edit unmounts after actually
 * leaving the flow.
 */
const permitEditServerPlateSeedByPermit: Record<string, string> = {};

type PermitEditRegistrationPlateLike = {
  countryCode?: string | null;
  plateText?: string | null;
} | null;

function buildPermitEditServerPlateSeedKey(
  orderLineIdWithVersion: string | undefined,
  registrationPlates:
    | ReadonlyArray<PermitEditRegistrationPlateLike>
    | undefined,
  creditCardId: string | null | undefined
): string {
  const plates = (registrationPlates ?? [])
    .filter((p): p is NonNullable<typeof p> => p != null)
    .map((p) => `${String(p.countryCode ?? '')}:${String(p.plateText ?? '')}`)
    .sort((a, b) => a.localeCompare(b))
    .join('|');
  return `${orderLineIdWithVersion ?? ''}@@${creditCardId ?? ''}@@${plates}`;
}

/**
 * Apply server registration plates to the redeem var only when server data
 * changed.
 */
export function syncPermitEditLicensePlatesFromServer(
  orderLineIdWithVersion: string | undefined,
  registrationPlates:
    | ReadonlyArray<PermitEditRegistrationPlateLike>
    | undefined,
  creditCardId: string | null | undefined,
  licensePlatesToApply: LicensePlate[]
): void {
  const id = orderLineIdWithVersion ?? '';
  const key = buildPermitEditServerPlateSeedKey(
    orderLineIdWithVersion,
    registrationPlates,
    creditCardId
  );
  if (permitEditServerPlateSeedByPermit[id] === key) {
    return;
  }
  permitEditServerPlateSeedByPermit[id] = key;
  usePermitStore
    .getState()
    .setSelectedLicensePlatesPermitRedeem(licensePlatesToApply);
}

export function clearPermitEditLicensePlateServerSeed(
  orderLineIdWithVersion?: string
): void {
  if (orderLineIdWithVersion) {
    delete permitEditServerPlateSeedByPermit[orderLineIdWithVersion];
    return;
  }
  clearPermitEditLicensePlateServerSeeds();
}

export function clearPermitEditLicensePlateServerSeeds(): void {
  for (const k of Object.keys(permitEditServerPlateSeedByPermit)) {
    delete permitEditServerPlateSeedByPermit[k];
  }
}

export const unlockMemberGroupSuccess: ReactiveVar<boolean> =
  makeVar<boolean>(false);
